import React, { useState, useMemo, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter, ZAxis
} from 'recharts';
import { Trial } from '../types';
import { BarChart2, ScatterChart as ScatterIcon } from 'lucide-react';
import { getExtraEndpointsForIndication } from '../constants';
import SocReportFilters from './soc/SocReportFilters';

interface ChartSectionProps {
  data: Trial[];
  selectedTrialIds: string[];
  indicationName?: string;
  statusFilter: 'All' | Trial['preferredStatus'];
  onStatusFilterChange: (status: 'All' | Trial['preferredStatus']) => void;
  guidelineFilter: 'nccn' | 'non-nccn';
  onGuidelineFilterChange: (guideline: 'nccn' | 'non-nccn') => void;
}

/** Bar/scatter category label: trial name → NCT → experimental drug name */
function chartCategoryLabel(trial: Trial): string {
  const name = (trial.trialName || '').trim();
  if (name) return name;
  const nct = (trial.nctId || '').trim();
  if (nct) return nct;
  const expDrug = (trial.expDrug || '').trim();
  return expDrug || '—';
}

const ChartSection: React.FC<ChartSectionProps> = ({
  data,
  indicationName,
  statusFilter,
  onStatusFilterChange,
  guidelineFilter,
  onGuidelineFilterChange,
}) => {
  const [chartType, setChartType] = useState<'bar' | 'scatter'>('bar');
  const [activeMetric, setActiveMetric] = useState<string>('ORR');
  const extraEndpoints = useMemo(() => getExtraEndpointsForIndication(indicationName), [indicationName]);
  const [activeSafetyMetric, setActiveSafetyMetric] = useState<'g35' | 'g5'>('g35');
  const [scatterXMetric, setScatterXMetric] = useState('orrExp');
  const [scatterYMetric, setScatterYMetric] = useState('orrCtrl');

  const safetyMetrics = useMemo(
    () => [
      { id: 'g35' as const, label: 'G3-5 AEs (%)', expKey: 'g35AEExp', ctrlKey: 'g35AECtrl', unit: '%' },
      { id: 'g5' as const, label: 'G5 / Death (%)', expKey: 'g5DeathExp', ctrlKey: 'g5DeathCtrl', unit: '%' },
    ],
    []
  );

  const availableSafetyMetrics = useMemo(
    () =>
      safetyMetrics.filter((metric) =>
        data.some(
          (trial) => {
            const expVal = (trial as any)[metric.expKey];
            const ctrlVal = (trial as any)[metric.ctrlKey];
            return expVal != null || ctrlVal != null;
          }
        )
      ),
    [data, safetyMetrics]
  );

  useEffect(() => {
    if (activeMetric !== 'AEs') return;
    if (availableSafetyMetrics.length === 0) return;
    if (!availableSafetyMetrics.find((metric) => metric.id === activeSafetyMetric)) {
      setActiveSafetyMetric(availableSafetyMetrics[0].id);
    }
  }, [activeMetric, availableSafetyMetrics, activeSafetyMetric]);

  const scatterMetrics = useMemo(
    () => {
      const base = [
        { id: 'orrExp', label: 'ORR Exp (%)', unit: '%', get: (trial: Trial) => trial.orrExp },
        { id: 'orrCtrl', label: 'ORR Ctrl (%)', unit: '%', get: (trial: Trial) => trial.orrCtrl },
        { id: 'crExp', label: 'CR (Exp)', unit: '%', get: (trial: Trial) => trial.crExp },
        { id: 'crCtrl', label: 'CR (Ctrl)', unit: '%', get: (trial: Trial) => trial.crCtrl },
        { id: 'mPFSExp', label: 'PFS (Exp)', unit: 'm', get: (trial: Trial) => trial.mPFSExp },
        { id: 'mPFSCtrl', label: 'PFS (Ctrl)', unit: 'm', get: (trial: Trial) => trial.mPFSCtrl },
        { id: 'mOSExp', label: 'OS (Exp)', unit: 'm', get: (trial: Trial) => trial.mOSExp },
        { id: 'mOSCtrl', label: 'OS (Ctrl)', unit: 'm', get: (trial: Trial) => trial.mOSCtrl },
        { id: 'g35AEExp', label: 'G3-5 AEs Exp (%)', unit: '%', get: (trial: Trial) => trial.g35AEExp },
        { id: 'g35AECtrl', label: 'G3-5 AEs Ctrl (%)', unit: '%', get: (trial: Trial) => trial.g35AECtrl },
        { id: 'g5DeathExp', label: 'G5 / Death Exp (%)', unit: '%', get: (trial: Trial) => trial.g5DeathExp },
        { id: 'g5DeathCtrl', label: 'G5 / Death Ctrl (%)', unit: '%', get: (trial: Trial) => trial.g5DeathCtrl },
      ];
      const extra = extraEndpoints.flatMap((ep) => [
        { id: ep.expKey as string, label: `${ep.label} (Exp)`, unit: '%' as const, get: (trial: Trial) => (trial as any)[ep.expKey] },
        { id: ep.ctrlKey as string, label: `${ep.label} (Ctrl)`, unit: '%' as const, get: (trial: Trial) => (trial as any)[ep.ctrlKey] },
      ]);
      return [...base, ...extra];
    },
    [extraEndpoints]
  );

  useEffect(() => {
    if (!scatterMetrics.find((metric) => metric.id === scatterXMetric)) {
      setScatterXMetric('orrExp');
    }
    if (!scatterMetrics.find((metric) => metric.id === scatterYMetric)) {
      setScatterYMetric('orrCtrl');
    }
  }, [scatterMetrics, scatterXMetric, scatterYMetric]);

  const filteredTrials = useMemo(
    () => {
      const byGuideline =
        guidelineFilter === 'nccn'
          ? data.filter((trial) => trial.preferredStatus !== 'N/A')
          : data.filter((trial) => trial.preferredStatus === 'N/A');
      const effectiveStatus = guidelineFilter === 'non-nccn' ? 'All' : statusFilter;
      return effectiveStatus === 'All'
        ? byGuideline
        : byGuideline.filter((trial) => trial.preferredStatus === effectiveStatus);
    },
    [data, guidelineFilter, statusFilter]
  );

  const activeExtraMetric = useMemo(
    () => extraEndpoints.find((e) => e.id === activeMetric),
    [extraEndpoints, activeMetric]
  );

  // Prepare data for Recharts - sorted by exp value (highest to lowest)
  const chartData = useMemo(() => {
    const safetyMetric = safetyMetrics.find((metric) => metric.id === activeSafetyMetric);
    const mapped = filteredTrials.map((trial) => {
      let expValue: number | null = null;
      let ctrlValue: number | null = null;
      if (activeExtraMetric) {
        expValue = (trial as any)[activeExtraMetric.expKey] ?? null;
        ctrlValue = (trial as any)[activeExtraMetric.ctrlKey] ?? null;
      } else {
        expValue =
          activeMetric === 'ORR'
            ? trial.orrExp
            : activeMetric === 'mPFS'
              ? trial.mPFSExp
              : activeMetric === 'mOS'
                ? trial.mOSExp
                : safetyMetric
                  ? (trial as any)[safetyMetric.expKey]
                  : null;
        ctrlValue =
          activeMetric === 'ORR'
            ? trial.orrCtrl
            : activeMetric === 'mPFS'
              ? trial.mPFSCtrl
              : activeMetric === 'mOS'
                ? trial.mOSCtrl
                : safetyMetric
                  ? (trial as any)[safetyMetric.ctrlKey]
                  : null;
      }
      return {
        name: chartCategoryLabel(trial),
        expValue,
        ctrlValue,
        id: trial.id,
        expDrug: trial.expDrug,
        ctrlDrug: trial.ctrlDrug,
      };
    });

    const filtered = mapped.filter((item) => {
      const hasValue = item.expValue !== null || item.ctrlValue !== null;
      if (!hasValue) return false;
      const expZero = item.expValue === 0;
      const ctrlZero = item.ctrlValue === 0;
      return !(expZero && ctrlZero);
    });
    return filtered.sort((a, b) => {
      const valA = a.expValue ?? a.ctrlValue ?? -Infinity;
      const valB = b.expValue ?? b.ctrlValue ?? -Infinity;
      return valB - valA;
    });
  }, [filteredTrials, activeMetric, activeSafetyMetric, safetyMetrics, activeExtraMetric]);

  const scatterData = useMemo(() => {
    const xMetric = scatterMetrics.find((metric) => metric.id === scatterXMetric);
    const yMetric = scatterMetrics.find((metric) => metric.id === scatterYMetric);
    if (!xMetric || !yMetric) return [];

    return filteredTrials
      .map((trial) => {
        const xValueRaw = xMetric.get(trial);
        const yValueRaw = yMetric.get(trial);
        const xValue = xValueRaw === null || xValueRaw === undefined ? null : Number(xValueRaw);
        const yValue = yValueRaw === null || yValueRaw === undefined ? null : Number(yValueRaw);
        return {
          name: chartCategoryLabel(trial),
          xValue,
          yValue,
          xLabel: xMetric.label,
          yLabel: yMetric.label,
          xUnit: xMetric.unit,
          yUnit: yMetric.unit,
          expDrug: trial.expDrug,
          ctrlDrug: trial.ctrlDrug,
          id: trial.id,
        };
      })
      .filter((item) => Number.isFinite(item.xValue) && Number.isFinite(item.yValue));
  }, [filteredTrials, scatterMetrics, scatterXMetric, scatterYMetric]);

  // Hide extremely long X-axis labels (tick text) to reduce clutter.
  // Hover tooltip still shows the full label.
  const tickLabelHideThreshold = useMemo(() => {
    const lens = chartData.map((d) => (d?.name ?? '').length).filter((n) => n > 0).sort((a, b) => a - b);
    if (!lens.length) return 999;
    const mid = Math.floor(lens.length / 2);
    const median = lens.length % 2 === 0 ? (lens[mid - 1] + lens[mid]) / 2 : lens[mid];
    // "Compared to others": hide anything substantially longer than the median.
    return Math.max(14, Math.floor(median + 8));
  }, [chartData]);

  const yAxisUnit =
    activeExtraMetric || activeMetric === 'ORR' || activeMetric === 'AEs'
      ? '%'
      : activeMetric === 'mPFS' || activeMetric === 'mOS'
        ? 'mos'
        : '';

  const selectedSafetyLabel = safetyMetrics.find((m) => m.id === activeSafetyMetric)?.label;

  /** Y-axis title: explicit ORR / AE labels; mPFS/mOS use (mos); extra endpoints use label + (%) */
  const barYAxisTitle =
    activeMetric === 'mPFS'
      ? 'mPFS (mos)'
      : activeMetric === 'mOS'
        ? 'mOS (mos)'
        : activeExtraMetric
          ? `${activeExtraMetric.label} (%)`
          : activeMetric === 'ORR'
            ? 'ORR (%)'
            : activeMetric === 'AEs'
              ? selectedSafetyLabel ?? 'Value (%)'
              : yAxisUnit === '%'
                ? 'Value (%)'
                : 'Value';

  const formatYAxisTick = (value: number) => {
    if (yAxisUnit === '%') return `${value}%`;
    if (activeMetric === 'mPFS' || activeMetric === 'mOS') {
      return Number.isFinite(value) ? String(value) : '';
    }
    if (!yAxisUnit) return String(value);
    return `${value} ${yAxisUnit}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const expDrug = payload[0]?.payload?.expDrug || 'Experimental';
      const ctrlDrug = payload[1]?.payload?.ctrlDrug || payload[0]?.payload?.ctrlDrug || 'Control';
      
      return (
        <div className="bg-white/95 backdrop-blur-md p-4 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-2xl text-sm max-w-[min(360px,calc(100vw-1.5rem))]">
          <p className="font-bold text-slate-900 mb-3 text-base break-words">{label}</p>
          <div className="space-y-2">
            <div className="flex justify-between items-start gap-3">
                <span className="text-slate-600 font-medium text-sm break-words min-w-0 flex-1">{expDrug}</span>
                <span className="text-indigo-600 font-bold bg-indigo-50 px-2 py-0.5 rounded-md shrink-0 tabular-nums">
                    {payload[0].value}{activeExtraMetric || activeMetric.includes('ORR') || activeMetric.includes('AE') ? '%' : ' mos'}
                </span>
            </div>
            {payload[1] && (
                <div className="flex justify-between items-start gap-3">
                    <span className="text-slate-600 font-medium text-sm break-words min-w-0 flex-1">{ctrlDrug}</span>
                    <span className="text-slate-600 font-bold bg-gray-50 px-2 py-0.5 rounded-md shrink-0 tabular-nums">
                        {payload[1].value}{activeExtraMetric || activeMetric.includes('ORR') || activeMetric.includes('AE') ? '%' : ' mos'}
                    </span>
                </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomScatterTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const trialName = data?.name || 'Unknown Trial';
      const xLabel = data?.xLabel || 'X';
      const yLabel = data?.yLabel || 'Y';
      const xUnit = data?.xUnit || '';
      const yUnit = data?.yUnit || '';
      
      return (
        <div className="bg-white/95 backdrop-blur-md p-4 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-2xl text-sm max-w-[min(360px,calc(100vw-1.5rem))]">
          <p className="font-bold text-slate-900 mb-3 text-base break-words">{trialName}</p>
          <div className="space-y-2">
            <div className="flex justify-between items-start gap-3">
                <span className="text-slate-600 font-medium text-sm break-words min-w-0 flex-1">{xLabel}</span>
                <span className="text-indigo-600 font-bold bg-indigo-50 px-2 py-0.5 rounded-md shrink-0 tabular-nums">
                    {data.xValue}{xUnit ? ` ${xUnit}` : ''}
                </span>
            </div>
            <div className="flex justify-between items-start gap-3">
                <span className="text-slate-600 font-medium text-sm break-words min-w-0 flex-1">{yLabel}</span>
                <span className="text-slate-600 font-bold bg-gray-50 px-2 py-0.5 rounded-md shrink-0 tabular-nums">
                    {data.yValue}{yUnit ? ` ${yUnit}` : ''}
                </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = () => (
    <div className="flex items-center justify-center gap-4 text-xs font-semibold text-slate-500">
      <span className="inline-flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-indigo-600" />
        Experimental Arm
      </span>
      <span className="inline-flex items-center gap-2">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-300" />
        Control Arm
      </span>
    </div>
  );

  return (
    <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8 mb-4 transition-all hover:shadow-lg">
      <SocReportFilters
        className="mb-5"
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        guidelineFilter={guidelineFilter}
        onGuidelineFilterChange={onGuidelineFilterChange}
      />


      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-6">
        <div>
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                Comparative Visuals
            </h3>
            <p className="text-sm text-slate-500 mt-1">Analyze efficacy and safety across multiple trials.</p>
        </div>
        
        <div className="flex flex-wrap gap-4 items-center">
            {chartType !== 'scatter' && (
              <div className="bg-slate-100 p-1.5 rounded-xl flex flex-wrap gap-1">
                {(['ORR', 'mPFS', 'mOS', 'AEs'] as const).map((metric) => (
                  <button
                    key={metric}
                    onClick={() => setActiveMetric(metric)}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      activeMetric === metric 
                        ? 'bg-white text-indigo-600 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                    }`}
                  >
                    {metric === 'ORR' ? 'ORR (%)' : metric === 'AEs' ? 'AEs' : metric}
                  </button>
                ))}
                {extraEndpoints.map((ep) => (
                  <button
                    key={ep.id}
                    onClick={() => setActiveMetric(ep.id)}
                    className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                      activeMetric === ep.id 
                        ? 'bg-white text-indigo-600 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                    }`}
                  >
                    {ep.label}
                  </button>
                ))}
              </div>
            )}

            {chartType !== 'scatter' && activeMetric === 'AEs' && (
              <div className="min-w-[180px]">
                <label className="sr-only" htmlFor="safety-metric">
                  Safety metric
                </label>
                <select
                  id="safety-metric"
                  value={availableSafetyMetrics.length === 0 ? 'none' : activeSafetyMetric}
                  onChange={(event) => setActiveSafetyMetric(event.target.value as 'g35' | 'g5')}
                  disabled={availableSafetyMetrics.length === 0}
                  className="appearance-none w-full bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:bg-slate-50 disabled:cursor-not-allowed"
                >
                  {availableSafetyMetrics.length === 0 ? (
                    <option value="none">No safety data</option>
                  ) : (
                    availableSafetyMetrics.map((metric) => (
                      <option key={metric.id} value={metric.id}>
                        {metric.label}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}

            {chartType === 'scatter' && (
              <>
                <div className="min-w-[180px]">
                  <label className="sr-only" htmlFor="scatter-y">
                    Scatter Y metric
                  </label>
                  <select
                    id="scatter-y"
                    value={scatterYMetric}
                    onChange={(event) => setScatterYMetric(event.target.value)}
                    className="appearance-none w-full bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
                  >
                    {scatterMetrics.map((metric) => (
                      <option key={`y-${metric.id}`} value={metric.id}>
                        Y: {metric.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[180px]">
                  <label className="sr-only" htmlFor="scatter-x">
                    Scatter X metric
                  </label>
                  <select
                    id="scatter-x"
                    value={scatterXMetric}
                    onChange={(event) => setScatterXMetric(event.target.value)}
                    className="appearance-none w-full bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
                  >
                    {scatterMetrics.map((metric) => (
                      <option key={`x-${metric.id}`} value={metric.id}>
                        X: {metric.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Chart Type Segmented Control */}
            <div className="bg-slate-100 p-1.5 rounded-xl flex gap-1">
                <button 
                    onClick={() => setChartType('bar')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${chartType === 'bar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <BarChart2 className="w-3.5 h-3.5" />
                </button>
                <button 
                    onClick={() => setChartType('scatter')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${chartType === 'scatter' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <ScatterIcon className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
      </div>

      <div className="flex flex-col h-[560px] min-h-[400px] w-full bg-slate-50/30 rounded-2xl border border-gray-100/50 p-4 sm:min-h-[420px]">
        {chartType === 'bar' && (
          <div className="flex justify-center shrink-0 pb-2">
            <CustomLegend />
          </div>
        )}
        <div className="flex-1 min-h-0 w-full h-full min-h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
                <BarChart data={chartData} margin={{ top: 12, right: 24, left: 8, bottom: 64 }} barCategoryGap="12%">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis
                        dataKey="name"
                        interval={0}
                        tickFormatter={(value) => {
                          const s = String(value ?? '');
                          return s.length > tickLabelHideThreshold ? '' : s;
                        }}
                        angle={-38}
                        textAnchor="end"
                        height={58}
                        tick={{ fill: '#64748b', fontSize: 10, fontWeight: 500 }}
                        axisLine={false}
                        tickLine={false}
                        dy={6}
                    />
                    <YAxis 
                        width={activeMetric === 'mPFS' || activeMetric === 'mOS' ? 52 : 48}
                        tick={{fill: '#64748b', fontSize: 12}} 
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatYAxisTick}
                        label={{
                          value: barYAxisTitle,
                          angle: -90,
                          position: 'insideLeft',
                          fill: '#64748b',
                          fontSize: 12,
                        }}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{fill: 'rgba(241, 245, 249, 0.4)'}} />
                    <Bar name="Experimental Arm" dataKey="expValue" fill="#4f46e5" radius={[6, 6, 0, 0]} maxBarSize={56} animationDuration={1000} />
                    <Bar name="Control Arm" dataKey="ctrlValue" fill="#cbd5e1" radius={[6, 6, 0, 0]} maxBarSize={56} animationDuration={1000} />
                </BarChart>
            ) : scatterData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  No scatter data available for the selected metrics.
                </div>
            ) : (
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                        type="number" 
                        dataKey="xValue" 
                        name="X" 
                        unit={scatterMetrics.find((metric) => metric.id === scatterXMetric)?.unit || ''} 
                        tick={{fontSize: 12, fill: '#64748b'}} 
                        label={{ value: scatterMetrics.find((metric) => metric.id === scatterXMetric)?.label || 'X', position: 'bottom', offset: 0, fill: '#64748b', fontSize: 12 }} 
                        axisLine={false}
                        tickLine={false}
                        domain={['dataMin', 'dataMax']}
                    />
                    <YAxis 
                        type="number" 
                        dataKey="yValue" 
                        name="Y" 
                        unit={scatterMetrics.find((metric) => metric.id === scatterYMetric)?.unit || ''} 
                        tick={{fontSize: 12, fill: '#64748b'}} 
                        label={{ value: scatterMetrics.find((metric) => metric.id === scatterYMetric)?.label || 'Y', angle: -90, position: 'left', fill: '#64748b', fontSize: 12 }} 
                        axisLine={false}
                        tickLine={false}
                        domain={['dataMin', 'dataMax']}
                        reversed={false}
                    />
                    <ZAxis dataKey="name" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<CustomScatterTooltip />} />
                    <Scatter 
                        name="Trials" 
                        data={scatterData} 
                        fill="#4f46e5" 
                        animationDuration={1000}
                    />
                </ScatterChart>
            )}
            </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ChartSection;
